import React, {useEffect, useMemo, useState} from 'react';
import axios from "axios";
import {ColumnDef} from '@tanstack/react-table';
import {Link, useNavigate, useParams} from 'react-router-dom';

import {DataGrid, KeenIcon} from '@/components';
import {Loading} from '@/layouts/buddy/loading';
import {enqueueSnackbar} from "notistack";
import {MatchDetailsData} from "@/pages/buddy/types/MatchDetailsData.ts";
import {JobDetailsData} from "@/pages/buddy/types/JobDetailsData.ts";
import {formatDate} from "@/pages/buddy/utils/Date.tsx";
import {getProgressColor} from "@/pages/buddy/utils/ProgressColor.tsx";

const Jobs = () => {
    const storageFilterId = 'jobs-filter';

    const {p_id} = useParams(); // Access `p_id` from URL
    // State to store the companies data and loading/error state
    const [data, setData] = useState<JobDetailsData[]>([]);
    const [loading, setLoading] = useState(true);
    const [isActiveFilter, setIsActiveFilter] = useState(true); // Set to true by default

    // Define the API base URL dynamically from environment variables
    const API_JOBS = `${import.meta.env.VITE_APP_API_URL}/jobs`;

    const navigate = useNavigate();

    const fetchJobsData = async () => {
        try {
            // Example API request to fetch data
            const response = await axios.get<JobDetailsData[]>(`${API_JOBS}/${p_id}`, {
                headers: {
                    Accept: 'application/json',  // Ensures JSON response
                },
            });
            setData(response.data);
            setLoading(false);
        } catch (err) {
            enqueueSnackbar(`Failed to fetch companies data: ${err}`, {
                variant: 'solid',
                state: 'danger'
            });
            setLoading(false);
        }
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchJobsData();
    }, []);

    const columns = useMemo<ColumnDef<JobDetailsData>[]>(() => [
        {
            accessorFn: (row) => row.name,
            id: 'name',
            header: () => 'Job',
            enableSorting: true,
            cell: (info) => (
                <div className="flex flex-col gap-2">
                    <Link
                        className="leading-none font-medium text-sm text-gray-900 hover:text-primary"
                        to={`/job/${info.row.original.p_id}`}
                    >
                        {info.row.original.name}
                    </Link>
                </div>
            ),
            meta: {
                className: 'min-w-[350px]',
                cellClassName: 'text-gray-700 font-normal'
            }
        },
        {
            accessorFn: (row) => row.matches?.[0] as MatchDetailsData | undefined,
            id: 'best_match',
            header: () => 'Best Match',
            enableSorting: false,
            cell: (info) => {
                const match = info.row.original.matches?.[0];
                if (!match) return 'No Matches';

                const value = match.overall_soft_score_relative;
                const percentage = value !== undefined && value !== null ? Math.round(value * 100) : null;

                return (
                    <div className="flex flex-col gap-1 text-sm font-normal">
                        <div className="flex items-center gap-2">
                            <button
                                className="btn btn-sm btn-icon btn-clear btn-light"
                                onClick={() => navigate(`/match/${match.p_id}`)}
                            >
                                <KeenIcon icon="eye"/>
                            </button>
                            <Link
                                className="leading-none hover:text-primary"
                                to={`/match/${match.p_id}`}
                            >
                                {match.candidate_first_name} {match.candidate_last_name}
                            </Link>
                            {percentage !== null && (
                                <span className="text-gray-600">{percentage}%</span>
                            )}
                        </div>
                        {percentage !== null && (
                            <div className="flex-1 bg-gray-200 rounded-full h-1.5">
                                <div
                                    className="h-1.5 rounded-full"
                                    style={{
                                        width: `${percentage}%`,
                                        backgroundColor: getProgressColor(percentage),
                                    }}
                                ></div>
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            id: 'all-candidates',
            header: () => 'Matched Candidates',
            enableSorting: false,
            cell: ({ row }) => (
                <div className="flex text-sm font-normal items-center gap-2">
                    <button
                        className="btn btn-sm btn-icon btn-clear btn-light"
                        onClick={() => navigate(`/job/${row.original.p_id}/candidates`)}
                    >
                        <KeenIcon icon="eye" />
                    </button>
                    <Link
                        className="leading-none hover:text-primary"
                        to={`/job/${row.original.p_id}/candidates`}
                    >
                        {row.original.matches ? row.original.matches.length : 0} Candidates
                    </Link>
                </div>
            ),
        },
        {
            accessorFn: (row) => row.updated_at,
            id: 'lastModified',
            enableSorting: true,
            header: () => 'Last Modified',
            cell: (info) => {
                const value = info.getValue() as string | null;
                return formatDate(value);
            },
            meta: {
                className: 'w-[350px]',
                cellClassName: 'text-gray-700 font-normal'
            }
        },
        {
            id: 'edit',
            header: () => '',
            enableSorting: false,
            cell: ({row}) => (
                <button
                    className="btn btn-sm btn-icon btn-clear btn-light"
                    onClick={() => navigate(`/job/${row.original.p_id}`)}
                >
                    <KeenIcon icon="notepad-edit"/>
                </button>
            ),
            meta: {
                className: 'w-[60px]',
            },
        },
        {
            id: 'delete',
            header: () => '',
            enableSorting: false,
            cell: ({row}) => (
                <button
                    className="btn btn-sm btn-icon btn-clear btn-light"
                    onClick={() => alert(`Clicked on delete for ${row.original.name}`)}
                >
                    <KeenIcon icon="trash"/>
                </button>
            ),
            meta: {
                className: 'w-[60px]',
            },
        },
    ], []);

    // Initialize search term from localStorage if available
    const [searchTerm, setSearchTerm] = useState(() => {
        return localStorage.getItem(storageFilterId) || '';
    });

    // Update localStorage whenever the search term changes
    useEffect(() => {
        localStorage.setItem(storageFilterId, searchTerm);
    }, [searchTerm]);

    // Filtered data based on search term
    const filteredData = useMemo(() => {
        return data.filter(company => {
            const jobsSearch = (company.name ?? "").toLowerCase().includes(searchTerm.toLowerCase())
            const jobsActiveFilter = !isActiveFilter || company.is_active;
            return jobsSearch && jobsActiveFilter;
        });
    }, [searchTerm, isActiveFilter, data]);

    useEffect(() => {
        //console.log("Filtered data:", filteredData);  // Log filtered data on every change
    }, [filteredData]);

    if (loading) return <Loading loading={loading}/>;

    return (
        <div className="card card-grid min-w-full">
            <div className="card-header flex-wrap py-5">
                <h3 className="card-title">Jobs</h3>
                <div className="flex gap-6">
                    <div className="relative">
                        <KeenIcon
                            icon="magnifier"
                            className="leading-none text-md text-gray-500 absolute top-1/2 left-0 -translate-y-1/2 ml-3"
                        />
                        <input
                            type="text"
                            placeholder="Search Jobs"
                            className="input input-sm pl-8"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)} // Update search term
                        />
                    </div>
                    <label className="switch switch-sm">
                        <input
                            name="check"
                            type="checkbox"
                            checked={isActiveFilter} // Set to true initially
                            onChange={() => setIsActiveFilter(!isActiveFilter)}
                            className="order-2"
                        />
                        <span className="switch-label order-1">Only Active Jobs</span>
                    </label>
                </div>
            </div>

            <div className="card-body">
                <DataGrid
                    key={`${searchTerm}-${isActiveFilter}`}  // Include isActiveFilter in the key
                    columns={columns}
                    data={filteredData}
                    pagination={{size: 10}}
                    sorting={[{id: 'name', desc: false}]}
                />
            </div>
        </div>
    );
};

export {Jobs};
